import React, { useEffect, useState } from 'react'
import { ActivityIndicator, TextStyle, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { Instance, cast } from 'mobx-state-tree'
import { useHistory, useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'

import { useStores } from '@trivie/core'
import { Box } from '@mui/material'
import { isCondition, isSectionHeader } from '@trivie/core/src/type-guards/isAction'
import { Node } from '@trivie/core/src/models/workflow/Node'
import { Header, SvgIcon, color, iconArrowBack, iconWorkflowComplete } from '@trivie/ui-mobile'
import { PathStep } from '@trivie/ui-mobile/components/path-step'
import { UpNextStep } from '@trivie/ui-mobile/components/path-step/up-next-step'
import { Text } from '@trivie/ui-mobile/components/text'
import { CountdownTimer } from '@trivie/ui-mobile/components/countdown-timer'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'

const defaultTopicImage = require('../topic-detail-screen/default-topic-image.png')

const useQuery = () => {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

export const PathDetailScreen: React.FunctionComponent<any> = observer(() => {
  const history = useHistory()
  const query = useQuery()
  const params = {
    assignment_id: Number(query.get('assignment_id')) || null,
    subscription_user_id: Number(query.get('subscription_user_id')) || null,
    path_id: Number(query.get('path_id')) || null,
  }

  const { userStore, rootCache, libraryStore, pathStore, resourceStore } = useStores()
  const { setStartViewed, setEndViewed, markAsViewed, getSourceDetails } = resourceStore
  const { user, setSplashNotifications, checkNotifications } = userStore
  const { selectedPath, getPathForUser } = pathStore

  const [loading, setLoading] = useState(true)
  const [viewingTextSource, setViewingTextSource] = useState<any>()

  const onFocus = () => {
    const resourceData = localStorage.getItem('viewing-resource')
    if (resourceData) {
      const resource = JSON.parse(resourceData)
      if (resourceData) {
        localStorage.removeItem('viewing-resource')
        setEndViewed(DateTime.now().toUTC().toISO())
        markAsViewed(resource.resource_id, resource.metadata).then(() => {
          load()
        })
      }
    }
  }

  useEffect(() => {
    window.addEventListener('focus', onFocus)
    onFocus()
    load()
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  const load = () => {
    rootCache.invalidateAction('getPathsForUser', pathStore)
    rootCache.invalidateAction('getQuizzesForUser', libraryStore)
    rootCache.invalidateAction('getTopicsForUser', libraryStore)
    rootCache.invalidateAction('getResourcesForUser', libraryStore)
    setLoading(true)
    getPathForUser(userStore.user!.id, params.path_id, params).then((data: any) => {
      setLoading(false)
      if (data?.kind === 'not-found') {
        history.goBack()
      }
    })
    checkNotifications(userStore.user!.id)
  }

  const goToContent = (node: Instance<typeof Node>, step: any) => {
    const { assignment_id, subscription_user_id, path_id, topic_id, content_user_id } =
      node.metadata!
    switch (step.object_type) {
      case 'Survey':
        if (user) {
          history.push(
            `/survey/?survey_id=${step.object_id}&content_user_id=${content_user_id}&subscription_user_id=${subscription_user_id}&path_id=${path_id}&assignment_id=${assignment_id}`,
          )
        }

        break
      case 'Topic':
        if (userStore.isSubscriptionWorkspace) {
          history.push(
            `/library/topic/?subscription_user_id=${subscription_user_id}&topic_id=${node.object_id}&path_id=${path_id}&content_user_id=${content_user_id}`,
          )
        } else {
          history.push(
            `/library/topic/?assignment_id=${assignment_id}&topic_id=${node.object_id}&path_id=${path_id}&content_user_id=${content_user_id}`,
          )
        }

        break
      case 'Quiz':
        if (userStore.isSubscriptionWorkspace) {
          history.push(
            `/library/quiz/?subscription_user_id=${subscription_user_id}&topic_id=${topic_id}&quiz_id=${node.object_id}&path_id=${path_id}&content_user_id=${content_user_id}`,
          )
        } else {
          history.push(
            `/library/quiz/?assignment_id=${assignment_id}&topic_id=${topic_id}&quiz_id=${node.object_id}&path_id=${path_id}&content_user_id=${content_user_id}`,
          )
        }

        break
      case 'Source':
        setStartViewed(DateTime.now().toUTC().toISO())
        getSourceDetails(node.object_id).then((data) => {
          if (data && node.metadata) {
            if (data?.raw_text) {
              setViewingTextSource({ ...data, metadata: node.metadata })
            } else if (data?.isVideo || data?.isAudio) {
              localStorage.setItem(
                'viewing-resource',
                JSON.stringify({ resource_id: data.resource_id, metadata: node.metadata }),
              )
              history.push(
                `/library/source/?source_id=${data.source_id}&assignment_id=${node.metadata.assignment_id}&content_user_id=${node.metadata.content_user_id}&path_id=${node.metadata.path_id}&quiz_id=${node.metadata.quiz_id}&topic_id=${node.metadata.topic_id}`,
              )
            } else {
              localStorage.setItem(
                'viewing-resource',
                JSON.stringify({ resource_id: data.resource_id, metadata: node.metadata }),
              )
              window.open(
                // @ts-ignore
                data.converted_pdf_url ? data.converted_pdf_url : data.filename_or_url,
                '_blank',
                'noopener',
              )
            }
          }
        })
        break
    }
  }

  const goToBadge = (badge, complete) => {
    setSplashNotifications(
      cast([
        {
          notification_type: 'badge',
          badge_icon_url: badge.icon_url,
          label: badge.label,
          description: badge.description,
          locked: !complete,
          hide_celebration: true,
        },
      ]),
    )
  }

  const renderSteps = () => {
    // Flatten all steps from all nodes into a single array with their associated node
    const allSteps: Array<{ node: Instance<typeof Node>; step: any }> = []
    selectedPath?.workflow_json.nodes.forEach((node) => {
      node.steps.forEach((step) => {
        // Filter out delay/wait actions
        if (
          step.action_type !== 'wait_until' &&
          step.action_type !== 'delay' &&
          step.action_type !== 'delay_from_assignment_start_date' &&
          step.action_type !== 'delay_from_user_received_assignment'
        ) {
          allSteps.push({ node, step })
        }
      })
    })

    // Render each step with proper separator logic
    return allSteps.map(({ node, step }, index) => {
      const previousStep = index > 0 ? allSteps[index - 1].step : null

      const hideSeparator =
        (index === 0 && isCondition(step)) ||
        isSectionHeader(step) ||
        (previousStep && isSectionHeader(previousStep))

      return (
        <PathStep
          key={`step-${index}`}
          hideSeparator={hideSeparator}
          goToBadge={(badge) => goToBadge(badge, node.is_completed)}
          node={node}
          step={step}
          onPress={() => goToContent(node, step)}
        />
      )
    })
  }

  return (
    <div style={CONTAINER}>
      <div style={HEADER_STICKY_CONTAINER}>
        <div style={HEADER_FLEX_CONTAINER}>
          <div style={HEADER}>
            <Header leftIcon={iconArrowBack} onLeftPress={history.goBack} center />
          </div>
        </div>
      </div>
      {loading ? (
        <ActivityIndicator
          color="#999"
          style={{ flex: 1, height: window.innerHeight - 100 }}
          size="small"
        />
      ) : (
        <div style={CONTENT}>
          <div style={{ position: 'relative', height: 141, borderRadius: 16, overflow: 'hidden' }}>
            <img
              src={selectedPath?.image_url || defaultTopicImage}
              style={{ height: 141, width: '100%', objectFit: 'cover', position: 'absolute' }}
            />
            <div style={DUE_CONTAINER}>
              {selectedPath?.end_datetime &&
                !selectedPath?.workflow_json?.completed &&
                !userStore.isSubscriptionWorkspace && (
                  <CountdownTimer
                    locale={user?.language ?? 'en'}
                    date={selectedPath.end_datetime}
                  />
                )}
            </div>
          </div>
          <Text
            variant="headline"
            text={selectedPath?.name}
            style={{ ...PATH_NAME, marginBottom: selectedPath?.description ? 20 : 0 }}
          />
          {selectedPath?.description ? (
            <Text
              variant="body2"
              text={selectedPath?.description}
              style={{ color: color.shade70 }}
            />
          ) : null}
          <View style={DIVIDER} />
          {selectedPath?.workflow_json?.completed && (
            <View style={COMPLETED}>
              <Text tx="Path.complete" text="Path Complete!" variant="titleEmphasized" />
              <SvgIcon src={iconWorkflowComplete} size={42} />
            </View>
          )}
          {!selectedPath?.workflow_json?.completed &&
            (selectedPath?.workflow_json?.upNext?.step?.user_visible === undefined ||
              selectedPath?.workflow_json?.upNext?.step?.user_visible) && (
              <>
                <Text
                  tx="Quiz.upNext"
                  variant="body1Medium"
                  style={{ color: color.shade50, marginVertical: 20 }}
                />
                <UpNextStep
                  step={selectedPath?.workflow_json?.upNext?.step}
                  node={selectedPath?.workflow_json?.upNext?.node}
                  onPress={() =>
                    goToContent(
                      selectedPath?.workflow_json?.upNext?.node,
                      selectedPath?.workflow_json?.upNext?.step,
                    )
                  }
                />
              </>
            )}
          <Text
            text="Learning Path"
            tx="Path.learningPath"
            variant="body1Medium"
            style={{ marginBottom: 20, color: color.shade50 }}
          />
          {renderSteps()}
        </div>
      )}
      <Dialog
        maxWidth="sm"
        align="left"
        onClose={() => {
          setViewingTextSource(null)
          setEndViewed(DateTime.now().toUTC().toISO())
          markAsViewed(viewingTextSource?.resource_id, viewingTextSource?.metadata)
          load()
        }}
        title="Preview"
        open={Boolean(viewingTextSource)}
      >
        <Box flexDirection="column" display="flex" p={2} sx={{ whiteSpace: 'pre-wrap' }}>
          <Text style={{ marginBottom: 16 }} variant="headline" text={viewingTextSource?.title} />
          {viewingTextSource?.raw_text}
        </Box>
      </Dialog>
    </div>
  )
})

/**
 * Styling
 */
const CONTAINER: React.CSSProperties = {
  backgroundColor: color.white,
  height: '100%',
}
const CONTENT: React.CSSProperties = {
  flexDirection: 'column',
  justifyContent: 'center',
  display: 'flex',
  paddingBottom: 80,
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER: React.CSSProperties = { width: '100%' }
const DUE_CONTAINER: React.CSSProperties = {
  padding: 3,
  borderRadius: 16,
  alignSelf: 'flex-end',
  position: 'absolute',
  right: 18,
  bottom: 14,
  flexDirection: 'row',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
}
const PATH_NAME: TextStyle = {
  textAlign: 'center',
  marginTop: 20,
  marginBottom: 20,
  paddingLeft: 5,
  paddingRight: 5,
}
const COMPLETED: ViewStyle = {
  marginBottom: 20,
  paddingHorizontal: 20,
  paddingRight: 19,
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
}
const DIVIDER: ViewStyle = {
  marginTop: 20,
  marginBottom: 20,
  width: '100%',
  height: 1,
  backgroundColor: '#F5F5F7',
}
